import React, { useEffect, useRef, useState } from "react";

// AEON shared motion system (spring-feel plan, 2026-10-28).
// One damped-spring solver, used by every animation. No duration-based easing:
// a spring's settle time is an outcome of its stiffness/damping, not an input.
// Motion only — this module never touches data, trading logic, or API routes.
//
// Physics (semi-implicit Euler, mass = 1):
//   acceleration = -stiffness * (position - target) - damping * velocity
// Retargeting keeps the current position AND velocity, so a live value that
// changes mid-flight bends toward its new target instead of restarting.

// Presets. Damping ratio = damping / (2 * sqrt(stiffness)).
//   ratio >= 1: no visible overshoot. ratio < 1: visible overshoot (bouncy only).
export const SPRING_PRESETS = {
  // Live data: fast, critically damped, never bounces. Quote ticks, desk state.
  snappy: { stiffness: 280, damping: 34 },
  // Entrances, panels, edition swaps: firm with a faint settle.
  smooth: { stiffness: 170, damping: 22 },
  // Confirmations only — never on data. Small visible overshoot by design.
  bouncy: { stiffness: 200, damping: 12 },
  // Large layout shifts: slow, overdamped, nothing startling.
  gentle: { stiffness: 90, damping: 22 },
};

export function prefersReducedMotion() {
  try {
    return typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

// Hyperbolic rubber band, as in iOS overscroll (NOT logarithmic):
//   offset = (1 - 1 / (c * x / d + 1)) * d,  c = 0.55, d = container size.
// Resistance grows with pull; the offset approaches but never exceeds d.
// Sign-preserving: pulling past either end returns an offset in that direction.
// Pure function — unit-testable without a DOM.
export function rubberBand(pull, containerSize, coefficient = 0.55) {
  if (!Number.isFinite(pull) || pull === 0) return 0;
  const size = Math.abs(containerSize);
  if (!Number.isFinite(size) || size <= 0) return 0;
  const magnitude = (1 - 1 / (coefficient * (Math.abs(pull) / size) + 1)) * size;
  return pull < 0 ? -magnitude : magnitude;
}

// Advance one spring step. Pure function, exported for testing.
export function springStep(position, velocity, target, stiffness, damping, dt) {
  const acceleration = -stiffness * (position - target) - damping * velocity;
  const nextVelocity = velocity + acceleration * dt;
  const nextPosition = position + nextVelocity * dt;
  return { position: nextPosition, velocity: nextVelocity };
}

function settled(position, velocity, target) {
  return Math.abs(velocity) < 0.01 && Math.abs(position - target) < 0.01;
}

// Interruptible spring value for React.
// - Starts at the initial target (no animation on mount).
// - Every new target retargets the SAME spring, keeping position and velocity.
// - A fast tape never queues animations: the latest target always wins.
// - Reduced motion: values jump instantly to the target.
export function useSpringValue(target, presetName = "smooth") {
  const preset = SPRING_PRESETS[presetName] || SPRING_PRESETS.smooth;
  const stateRef = useRef({ position: target, velocity: 0, raf: 0 });
  const [value, setValue] = useState(target);

  useEffect(() => {
    const state = stateRef.current;
    if (prefersReducedMotion()) {
      state.position = target;
      state.velocity = 0;
      setValue(target);
      return undefined;
    }
    if (settled(state.position, state.velocity, target)) {
      if (state.position !== target) {
        state.position = target;
        state.velocity = 0;
        setValue(target);
      }
      return undefined;
    }
    let last = null;
    const tick = (now) => {
      if (last === null) last = now;
      // Clamp dt so a hidden tab (or a long frame) cannot fling the spring.
      const dt = Math.min((now - last) / 1000, 0.032);
      last = now;
      const next = springStep(
        state.position, state.velocity, target,
        preset.stiffness, preset.damping, dt,
      );
      state.position = next.position;
      state.velocity = next.velocity;
      if (settled(state.position, state.velocity, target)) {
        state.position = target;
        state.velocity = 0;
        setValue(target);
        return;
      }
      setValue(state.position);
      state.raf = requestAnimationFrame(tick);
    };
    state.raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(state.raf);
  }, [target, preset.stiffness, preset.damping]);

  return value;
}

// Entrance wrapper: children rise + fade in on mount with the smooth spring.
// The spring target starts at 0 and retargets to 1 on mount, so the same
// interruptible solver drives the entrance (opacity + translateY, i.e.
// transform/opacity only — no layout-thrashing properties). Reduced motion
// renders children statically (no animation at all).
export function SpringRise({ children, className = "" }) {
  const [target, setTarget] = useState(0);
  useEffect(() => { setTarget(1); }, []);
  const progress = useSpringValue(target, "smooth");
  if (prefersReducedMotion()) return <div className={className}>{children}</div>;
  return (
    <div
      className={className}
      style={{
        opacity: progress,
        transform: `translateY(${(1 - progress) * 16}px)`,
        willChange: "transform, opacity",
      }}
    >
      {children}
    </div>
  );
}

// Rubber-band overscroll for a real inner scroll container (touch only).
// At an edge, further drag is passed through rubberBand() and applied as a
// transform (never scrollTop — the browser owns real scrolling). On release
// the offset springs back to 0 with the smooth preset. Mouse-wheel scrolling
// is left to the browser. Attach to an element that actually overflows
// (overflow-x/overflow-y: auto); page-level scrolling is deliberately out
// of scope — the browser already owns that.
// Returns { ref, style } — spread style onto the same element as ref.
export function useRubberBandScroll(axis = "x") {
  const ref = useRef(null);
  const dragRef = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return undefined;
    const isX = axis === "x";
    const size = () => (isX ? el.clientWidth : el.clientHeight);
    const atStart = () => (isX ? el.scrollLeft <= 0 : el.scrollTop <= 0);
    const atEnd = () => (isX
      ? el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
      : el.scrollTop + el.clientHeight >= el.scrollHeight - 1);

    const onTouchStart = (event) => {
      const touch = event.touches[0];
      dragRef.current = { pos: isX ? touch.clientX : touch.clientY, pulled: 0, active: false };
    };
    const onTouchMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const touch = event.touches[0];
      const pos = isX ? touch.clientX : touch.clientY;
      // Dragging right/down at the start edge, or left/up at the end edge,
      // is overscroll; everything else is normal scrolling.
      const delta = pos - drag.pos;
      const overscrolling = (atStart() && delta > 0) || (atEnd() && delta < 0);
      if (!overscrolling) { drag.pulled = 0; drag.active = false; return; }
      drag.active = true;
      drag.pulled += delta;
      drag.pos = pos;
      setOffset(rubberBand(drag.pulled, size()));
    };
    const onTouchEnd = () => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag || !drag.active) return;
      // Spring back to 0 with the smooth preset, reusing the same solver.
      let position = drag.pulled;
      let velocity = 0;
      let last = null;
      const preset = SPRING_PRESETS.smooth;
      const tick = (now) => {
        if (last === null) last = now;
        const dt = Math.min((now - last) / 1000, 0.032);
        last = now;
        const next = springStep(position, velocity, 0, preset.stiffness, preset.damping, dt);
        position = next.position;
        velocity = next.velocity;
        if (settled(position, velocity, 0)) { setOffset(0); return; }
        setOffset(rubberBand(position, size()));
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [axis]);

  return {
    ref,
    style: offset === 0 ? undefined : {
      transform: axis === "x" ? `translateX(${offset}px)` : `translateY(${offset}px)`,
      willChange: "transform",
    },
  };
}
